import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Avatar,
  Button,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Stack,
  useTheme,
  Paper,
  Grid
} from '@mui/material';
import {
  Person,
  Email,
  Star,
  Favorite,
  Upgrade,
  CalendarMonth,
  LocationOn,
  CheckCircle
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

// Context
import { useAuth } from '../../contexts/AuthContext';
import { useRole, ROLES } from '../../contexts/RoleContext';
import { useFavorites } from '../../context/FavoritesContext';

// Components
import SubscriptionModal from '../modals/SubscriptionModal';

const EnhancedProfile = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { userRole, isPro } = useRole();
  const { favorites } = useFavorites();
  
  const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);

  const isProUser = isPro();
  const favoritesCount = Array.isArray(favorites) ? favorites.length : 0;

  // Format the account creation date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Get display name from email or metadata
  const getDisplayName = () => {
    if (currentUser?.user_metadata?.full_name) {
      return currentUser.user_metadata.full_name;
    }
    if (currentUser?.user_metadata?.name) {
      return currentUser.user_metadata.name;
    }
    if (currentUser?.email) {
      const username = currentUser.email.split('@')[0];
      return username.charAt(0).toUpperCase() + username.slice(1);
    }
    return 'User';
  };

  // Get tier info
  const getTierInfo = () => {
    if (userRole === ROLES.ADMIN) {
      return { label: 'Administrator', color: '#7c3aed', bgColor: '#ede9fe' };
    }
    if (userRole === ROLES.PRO_CUSTOMER) {
      return { label: 'Pro Member', color: '#d97706', bgColor: '#fef3c7' };
    }
    return { label: 'Basic Member', color: '#6b7280', bgColor: '#f3f4f6' };
  };

  const tierInfo = getTierInfo();

  return (
    <Box 
      sx={{ 
        minHeight: '100vh',
        bgcolor: '#f5f5f5',
        py: { xs: 2, md: 3 }
      }}
    >
      <Container maxWidth="lg" sx={{ px: { xs: 2, md: 3 } }}>
        {/* Profile Header Card */}
      <Paper
          elevation={0}
        sx={{
            bgcolor: 'white',
            borderRadius: 2,
            overflow: 'hidden',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            mb: 2
          }}
        >
          {/* Header Banner */}
          <Box
            sx={{
              height: { xs: 100, md: 140 },
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
            }}
          />
          
          {/* Profile Info */}
          <Box sx={{ px: { xs: 2, md: 3 }, pb: 3, mt: -5 }}>
            <Stack 
              direction={{ xs: 'column', sm: 'row' }} 
              spacing={2}
              alignItems={{ xs: 'center', sm: 'flex-end' }}
            >
              <Avatar
                sx={{
                  width: { xs: 90, md: 100 },
                  height: { xs: 90, md: 100 },
                  fontSize: '2rem',
                  fontWeight: 600,
                  bgcolor: 'white',
                  color: theme.palette.primary.main,
                  border: '3px solid white',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}
          >
            {currentUser?.email?.[0]?.toUpperCase() || 'U'}
          </Avatar>
              
              <Box sx={{ flex: 1, textAlign: { xs: 'center', sm: 'left' }, pt: { xs: 0, sm: 1 } }}>
                <Typography variant="h5" fontWeight="700" color="#1f2937" gutterBottom>
                  {getDisplayName()}
            </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {currentUser?.email}
            </Typography>
              <Chip
                  icon={isProUser ? <Star sx={{ fontSize: 14 }} /> : null}
                  label={tierInfo.label}
                  size="small"
                sx={{
                    bgcolor: tierInfo.bgColor,
                    color: tierInfo.color,
                    fontWeight: 600,
                    fontSize: '0.75rem'
                  }}
                />
          </Box>

              {!isProUser && userRole !== ROLES.ADMIN && (
            <Button
              variant="contained"
                  size="small"
              startIcon={<Upgrade />}
              onClick={() => setSubscriptionModalOpen(true)}
              sx={{
                    bgcolor: theme.palette.primary.main,
                    fontWeight: 600,
                    textTransform: 'none',
                    px: 2,
                    py: 0.75,
                    borderRadius: 1.5,
                    boxShadow: 'none',
                '&:hover': {
                      boxShadow: 'none',
                      bgcolor: theme.palette.primary.dark
                }
              }}
            >
              Upgrade to Pro
            </Button>
          )}
        </Stack>
          </Box>
      </Paper>

        {/* Info Cards Grid */}
        <Grid container spacing={2}>
          {/* Personal Information */}
          <Grid item xs={12} md={6}>
            <Card 
              elevation={0} 
            sx={{
                height: '100%',
                borderRadius: 2,
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }}
            >
              <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                <Typography variant="subtitle1" fontWeight="700" color="#1f2937" sx={{ mb: 2 }}>
                Personal Information
              </Typography>
                
                <List disablePadding>
                  <ProfileInfoItem
                    icon={<Person />}
                    label="Name"
                    value={getDisplayName()}
                  />
                  <Divider sx={{ my: 1.5 }} />
                  <ProfileInfoItem
                    icon={<Email />}
                  label="Email"
                    value={currentUser?.email || 'N/A'}
                  />
                  <Divider sx={{ my: 1.5 }} />
                  <ProfileInfoItem
                    icon={<CalendarMonth />}
                    label="Member Since"
                    value={formatDate(currentUser?.created_at)}
                  />
                  <Divider sx={{ my: 1.5 }} />
                  <ProfileInfoItem
                    icon={<LocationOn />}
                    label="Region"
                    value="New Jersey"
                  />
                </List>
              </CardContent>
            </Card>
              </Grid>

          {/* Account Details */}
              <Grid item xs={12} md={6}>
            <Card 
              elevation={0} 
              sx={{ 
                height: '100%',
                borderRadius: 2,
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }}
            >
              <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                <Typography variant="subtitle1" fontWeight="700" color="#1f2937" sx={{ mb: 2 }}>
                  Account Details
                </Typography>
                
                <List disablePadding>
                  <ListItem sx={{ px: 0, py: 0.75 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: 1.5,
                          bgcolor: tierInfo.bgColor,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <Star sx={{ fontSize: 18, color: tierInfo.color }} />
                      </Box>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="caption" color="text.secondary">
                          Membership Tier
                        </Typography>
                      }
                      secondary={
                        <Chip 
                          label={tierInfo.label} 
                          size="small"
                          sx={{ 
                            bgcolor: tierInfo.bgColor,
                            color: tierInfo.color,
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            height: 22
                          }} 
                        />
                      }
                    />
                  </ListItem>
                  
                  <Divider sx={{ my: 1.5 }} />
                  
                  <ListItem sx={{ px: 0, py: 0.75 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: 1.5,
                          bgcolor: '#dcfce7',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <CheckCircle sx={{ fontSize: 18, color: '#16a34a' }} />
                      </Box>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="caption" color="text.secondary">
                          Account Status
            </Typography>
                      }
                      secondary={
                        <Chip 
                          label="Active" 
                          size="small"
                          sx={{ 
                            bgcolor: '#dcfce7',
                            color: '#16a34a',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            height: 22
                          }} 
                        />
                      }
                    />
                  </ListItem>
                  
                  <Divider sx={{ my: 1.5 }} />
                  
                  <ListItem sx={{ px: 0, py: 0.75 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: 1.5,
                          bgcolor: '#fee2e2',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <Favorite sx={{ fontSize: 18, color: '#dc2626' }} />
                      </Box>
                </ListItemIcon>
                <ListItemText
                      primary={
                        <Typography variant="caption" color="text.secondary">
                          Saved Properties
                        </Typography>
                      }
                      secondary={
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography variant="h6" fontWeight="700" color="#1f2937">
                            {favoritesCount}
                          </Typography>
                          {favoritesCount > 0 && (
                            <Button 
                              size="small" 
                              onClick={() => navigate('/favorites')}
                              sx={{ 
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                p: 0,
                                minWidth: 'auto',
                                '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' }
                              }}
                            >
                              View All
                            </Button>
                          )}
                        </Stack>
                      }
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Upgrade Banner for Basic Users */}
          {!isProUser && userRole !== ROLES.ADMIN && (
            <Grid item xs={12}>
              <Card 
                elevation={0}
                sx={{ 
                  borderRadius: 2,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  color: 'white',
                  overflow: 'hidden'
                }}
              >
                <CardContent sx={{ p: { xs: 2, md: 3 }, textAlign: 'center' }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} alignItems="center" spacing={2} justifyContent="center">
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: '50%',
                        bgcolor: 'rgba(255,255,255,0.15)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}
                    >
                      <Star sx={{ fontSize: 22, color: '#fbbf24' }} />
                    </Box>
                    <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                      <Typography variant="subtitle1" fontWeight="700">
                        Upgrade to Pro Membership
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Get full access to property details, upset values, and advanced analytics.
                      </Typography>
                    </Box>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<Upgrade />}
                      onClick={() => setSubscriptionModalOpen(true)}
                      sx={{
                        bgcolor: 'white',
                        color: theme.palette.primary.main,
                        fontWeight: 600,
                        textTransform: 'none',
                        px: 2.5,
                        py: 0.75,
                        borderRadius: 1.5,
                        boxShadow: 'none',
                        flexShrink: 0,
                        '&:hover': {
                          bgcolor: 'rgba(255,255,255,0.9)',
                          boxShadow: 'none'
                        }
                      }}
                    >
                      Upgrade — Free!
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Pro Member Card */}
          {isProUser && (
            <Grid item xs={12}>
              <Card 
                elevation={0}
                sx={{ 
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                  overflow: 'hidden'
                }}
              >
                <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                  <Stack direction="row" alignItems="center" spacing={2} justifyContent="center">
                    <Star sx={{ fontSize: 24, color: '#b45309' }} />
                    <Box>
                      <Typography variant="subtitle1" fontWeight="700" color="#92400e">
                        {userRole === ROLES.ADMIN ? 'Administrator Access' : 'Pro Member Benefits Active'}
                      </Typography>
                      <Typography variant="body2" color="#a16207">
                        Full access to all property details and advanced analytics.
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </Container>

      {/* Subscription Modal */}
      <SubscriptionModal
        open={subscriptionModalOpen}
        onClose={() => setSubscriptionModalOpen(false)}
        feature="Pro Membership"
      />
    </Box>
  );
};

// Reusable Profile Info Item Component
const ProfileInfoItem = ({ icon, label, value }) => {
  const theme = useTheme();
  
  return (
    <ListItem sx={{ px: 0, py: 0.75 }}>
      <ListItemIcon sx={{ minWidth: 40 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 1.5,
            bgcolor: `${theme.palette.primary.main}15`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {React.cloneElement(icon, { 
            sx: { fontSize: 18, color: theme.palette.primary.main } 
          })}
        </Box>
      </ListItemIcon>
      <ListItemText
        primary={
          <Typography variant="caption" color="text.secondary">
            {label}
          </Typography>
        }
        secondary={
          <Typography variant="body2" fontWeight="600" color="#1f2937">
            {value}
          </Typography>
        }
      />
    </ListItem>
  );
};

export default EnhancedProfile;
